import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { TradeLeg } from './TradeEntryDialog';

export type CloseReason = string;

export interface CloseReasonOption {
  value: string;
  label: string;
}

const DEFAULT_REASONS: CloseReasonOption[] = [
  { value: 'closed_profit', label: 'Closed at profit' },
  { value: 'closed_loss', label: 'Closed at loss' },
  { value: 'expired', label: 'Expired worthless' },
  { value: 'assigned', label: 'Assigned' },
];

export const WHEEL_REASONS: CloseReasonOption[] = [
  { value: 'bought_back', label: 'Bought back' },
  { value: 'expired', label: 'Expired worthless' },
  { value: 'assigned', label: 'Assigned' },
];

export const SHARES_REASONS: CloseReasonOption[] = [
  { value: 'sold', label: 'Sold' },
];

export interface CloseTradeData {
  close_reason: CloseReason;
  close_date: string;
  close_price?: number;
  contracts?: number;
}

interface CloseTradeDialogProps {
  open: boolean;
  ticker: string;
  strategyName: string;
  legs: TradeLeg[];
  totalContracts: number;
  entryPrice: number;
  closeReasons?: CloseReasonOption[];
  tradeDirection?: 'credit' | 'debit';
  qtyLabel?: string;
  onClose: () => void;
  onSubmit: (data: CloseTradeData) => Promise<void>;
}

const CloseTradeDialog: React.FC<CloseTradeDialogProps> = ({
  open, ticker, strategyName, legs, totalContracts, entryPrice, closeReasons, tradeDirection, qtyLabel, onClose, onSubmit,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const reasons = closeReasons || DEFAULT_REASONS;
  const [reason, setReason] = useState<CloseReason>(reasons[0].value);
  const [closeDate, setCloseDate] = useState(new Date().toISOString().split('T')[0]);
  const [closePrice, setClosePrice] = useState('');
  const [contractsToClose, setContractsToClose] = useState(totalContracts.toString());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isShareMode = qtyLabel === 'Shares to close';
  const needsPrice = reason !== 'expired' && reason !== 'assigned';
  const cp = parseFloat(closePrice);
  const isCredit = !isShareMode && (tradeDirection === 'credit' || (!tradeDirection && legs.every((l) => l.side === 'sell')));
  const qty = parseInt(contractsToClose || '0');
  const pnl = (() => {
    if (!needsPrice || isNaN(cp) || qty <= 0) return null;
    if (isShareMode) {
      // Shares: (sale_price - cost_basis) × shares
      return Math.round((cp - entryPrice) * qty * 100) / 100;
    }
    // Options: credit = (entry - close) × contracts × 100, debit = (close - entry) × contracts × 100
    const diff = isCredit ? (entryPrice - cp) : (cp - entryPrice);
    return Math.round(diff * qty * 100 * 100) / 100;
  })();

  const handleConfirm = async () => {
    if (needsPrice && (isNaN(cp) || cp < 0)) {
      setError('Enter a valid close price');
      return;
    }
    const c = parseInt(contractsToClose, 10);
    if (isNaN(c) || c <= 0 || c > totalContracts) {
      setError(`Enter 1-${totalContracts} ${isShareMode ? 'shares' : 'contracts'}`);
      return;
    }
    setError('');
    setLoading(true);
    try {
      await onSubmit({
        close_reason: reason,
        close_date: closeDate,
        close_price: needsPrice ? cp : undefined,
        contracts: c,
      });
      onClose();
    } catch (e: any) {
      setError(e.message || 'Failed to close trade');
    } finally {
      setLoading(false);
    }
  };

  const legLabel = (leg: TradeLeg) => {
    const prefix = leg.side === 'buy' ? '+' : '-';
    const type = leg.option_type === 'C' ? 'Call' : 'Put';
    return `${prefix} $${leg.strike} ${type}`;
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen={fullScreen} maxWidth="xs" fullWidth>
      <DialogTitle>Close — {strategyName}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {ticker} &middot; {totalContracts} {isShareMode ? 'shares' : `contract${totalContracts > 1 ? 's' : ''}`} &middot; Entry ${entryPrice.toFixed(2)}{isShareMode ? '' : '/sh'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, mb: 2, flexWrap: 'wrap' }}>
          {legs.map((leg, i) => (
            <Chip key={i} label={legLabel(leg)} size="small" variant="outlined" />
          ))}
        </Box>

        <TextField
          label={qtyLabel || 'Contracts to close'}
          type="number"
          value={contractsToClose}
          onChange={(e) => setContractsToClose(e.target.value)}
          inputProps={{ min: 1, max: totalContracts, step: 1 }}
          size="small"
          fullWidth
          sx={{ mb: 2 }}
        />

        <FormControl component="fieldset">
          <RadioGroup value={reason} onChange={(e) => setReason(e.target.value)}>
            {reasons.map((r) => (
              <FormControlLabel key={r.value} value={r.value} control={<Radio size="small" />} label={r.label} />
            ))}
          </RadioGroup>
        </FormControl>

        {needsPrice && (
          <TextField
            label="Close price (per share)"
            type="number"
            value={closePrice}
            onChange={(e) => setClosePrice(e.target.value)}
            inputProps={{ min: 0, step: 0.01 }}
            size="small"
            fullWidth
            sx={{ mt: 2 }}
          />
        )}

        <TextField
          label="Close date"
          type="date"
          value={closeDate}
          onChange={(e) => setCloseDate(e.target.value)}
          size="small"
          fullWidth
          InputLabelProps={{ shrink: true }}
          sx={{ mt: 2 }}
        />

        {pnl !== null && (
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 600, color: pnl >= 0 ? 'success.main' : 'error.main' }}>
            Est. P&L: {pnl >= 0 ? '+' : ''}${pnl.toFixed(0)}
          </Typography>
        )}

        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={loading}>
          {loading ? 'Saving...' : 'Close Position'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CloseTradeDialog;
